import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useResizableColumns, ResizeHandle, ColGroup } from '../hooks/useResizableColumns';

const API = process.env.REACT_APP_API_URL;

const emptyForm = () => ({ supplier_name: '' });

const inputStyle = { padding: '8px', borderRadius: '4px', border: '1px solid #ccc', width: '100%', boxSizing: 'border-box' };
const labelStyle = { fontSize: '12px', color: '#555', marginBottom: '3px', display: 'block' };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function Suppliers() {
  const [view, setView] = useState('directory');
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Drilldown
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [monthDeliveries, setMonthDeliveries] = useState({});

  const { widths, startResize, tableWidth } = useResizableColumns([200, 100, 120]);

  useEffect(() => { fetchSuppliers(); }, []);

  const fetchSuppliers = () => axios.get(`${API}/suppliers/`).then(r => setSuppliers(r.data)).catch(() => {});

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editing) {
      await axios.put(`${API}/suppliers/${editing}`, form);
      setEditing(null);
    } else {
      await axios.post(`${API}/suppliers/`, form);
    }
    setForm(emptyForm());
    setShowForm(false);
    fetchSuppliers();
  };

  const handleEdit = (s) => {
    setEditing(s.id);
    setForm({ supplier_name: s.supplier_name });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this supplier?')) {
      await axios.delete(`${API}/suppliers/${id}`);
      fetchSuppliers();
      if (selectedSupplier?.id === id) { setSelectedSupplier(null); setMonthlySummary(null); }
    }
  };

  const loadDrilldown = async (supplier) => {
    setSelectedSupplier(supplier);
    setMonthlySummary(null);
    setExpandedMonth(null);
    setMonthDeliveries({});
    const res = await axios.get(`${API}/suppliers/${supplier.id}/deliveries`);
    setMonthlySummary(res.data);
  };

  const loadMonthDeliveries = async (year, month) => {
    const key = `${year}-${month}`;
    if (expandedMonth === key) { setExpandedMonth(null); return; }
    setExpandedMonth(key);
    if (!monthDeliveries[key]) {
      const res = await axios.get(`${API}/suppliers/${selectedSupplier.id}/deliveries/${year}/${month}`);
      setMonthDeliveries(prev => ({ ...prev, [key]: res.data }));
    }
  };

  const thStyle = { padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd', position: 'relative', overflow: 'hidden', whiteSpace: 'nowrap', background: '#f5f5f5' };
  const tdStyle = { padding: '10px', borderBottom: '1px solid #eee', fontSize: '13px' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Suppliers</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ background: '#f0f0f0', borderRadius: '6px', padding: '3px', display: 'flex' }}>
            <button onClick={() => setView('directory')} style={{ padding: '5px 14px', background: view === 'directory' ? '#2c3e50' : 'transparent', color: view === 'directory' ? 'white' : '#555', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>Directory</button>
            <button onClick={() => setView('drilldown')} style={{ padding: '5px 14px', background: view === 'drilldown' ? '#2c3e50' : 'transparent', color: view === 'drilldown' ? 'white' : '#555', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>Drilldown</button>
          </div>
          {view === 'directory' && <button onClick={() => { setShowForm(true); setEditing(null); setForm(emptyForm()); }} style={{ padding: '8px 16px', background: '#2c3e50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>+ New Supplier</button>}
        </div>
      </div>

      {view === 'directory' && (
        <>
          {showForm && (
            <form onSubmit={handleSubmit} style={{ background: '#f9f9f9', border: '1px solid #eee', borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 16px' }}>{editing ? 'Edit Supplier' : 'New Supplier'}</h3>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}><label style={labelStyle}>Supplier Name *</label><input value={form.supplier_name} onChange={e => setForm({ supplier_name: e.target.value })} required style={inputStyle} /></div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="submit" style={{ padding: '8px 16px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{editing ? 'Update' : 'Create'}</button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); setForm(emptyForm()); }} style={{ padding: '8px 16px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
              </div>
            </form>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: tableWidth }}>
              <ColGroup widths={widths} />
              <thead>
                <tr>
                  <th style={thStyle}>Supplier Name<ResizeHandle onMouseDown={e => startResize(0, e)} /></th>
                  <th style={thStyle}>Actions<ResizeHandle onMouseDown={e => startResize(1, e)} /></th>
                </tr>
              </thead>
              <tbody>
                {suppliers.map(s => (
                  <tr key={s.id}>
                    <td style={tdStyle}><strong>{s.supplier_name}</strong></td>
                    <td style={tdStyle}>
                      <button onClick={() => handleEdit(s)} style={{ marginRight: '6px', padding: '4px 10px', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
                      <button onClick={() => handleDelete(s.id)} style={{ padding: '4px 10px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Delete</button>
                    </td>
                  </tr>
                ))}
                {suppliers.length === 0 && (
                  <tr><td colSpan={2} style={{ ...tdStyle, textAlign: 'center', color: '#aaa', padding: '40px' }}>No suppliers yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {view === 'drilldown' && (
        <div style={{ display: 'flex', gap: '20px', minHeight: '500px' }}>
          {/* Left */}
          <div style={{ width: '240px', flexShrink: 0, background: 'white', border: '1px solid #e8ecef', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', borderBottom: '1px solid #eee', background: '#f5f5f5', fontSize: '13px', fontWeight: '600', color: '#555' }}>Suppliers</div>
            {suppliers.map(s => (
              <div key={s.id} onClick={() => loadDrilldown(s)}
                style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', background: selectedSupplier?.id === s.id ? '#ebf5fb' : 'white', fontSize: '13px', fontWeight: '500', color: '#2c3e50' }}
                onMouseEnter={e => { if (selectedSupplier?.id !== s.id) e.currentTarget.style.background = '#f8f9fa'; }}
                onMouseLeave={e => { if (selectedSupplier?.id !== s.id) e.currentTarget.style.background = 'white'; }}
              >
                {s.supplier_name}
              </div>
            ))}
          </div>

          {/* Right */}
          <div style={{ flex: 1, background: 'white', border: '1px solid #e8ecef', borderRadius: '8px', padding: '20px' }}>
            {!selectedSupplier && <div style={{ color: '#aaa', padding: '40px', textAlign: 'center' }}>Select a supplier to view delivery history</div>}
            {selectedSupplier && !monthlySummary && <div style={{ color: '#aaa', padding: '40px', textAlign: 'center' }}>Loading…</div>}
            {selectedSupplier && monthlySummary && (
              <>
                <h3 style={{ margin: '0 0 16px', color: '#2c3e50' }}>{selectedSupplier.supplier_name}</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Year</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Month</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Uploads</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(monthlySummary.monthly_summary || []).map(row => {
                      const key = `${row.year}-${row.month}`;
                      const expanded = expandedMonth === key;
                      return (
                        <React.Fragment key={key}>
                          <tr onClick={() => loadMonthDeliveries(row.year, row.month)} style={{ cursor: 'pointer', background: expanded ? '#ebf5fb' : 'white' }}
                            onMouseEnter={e => { if (!expanded) e.currentTarget.style.background = '#f8f9fa'; }}
                            onMouseLeave={e => { if (!expanded) e.currentTarget.style.background = 'white'; }}
                          >
                            <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}>{row.year}</td>
                            <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}>{MONTHS[(row.month || 1) - 1]} {expanded ? '▲' : '▶'}</td>
                            <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee', textAlign: 'right' }}>{row.upload_count}</td>
                          </tr>
                          {expanded && monthDeliveries[key] && (
                            <tr>
                              <td colSpan={3} style={{ padding: '10px 24px', background: '#f0f7ff', borderBottom: '2px solid #d0e8ff' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                  <thead>
                                    <tr style={{ background: '#ddeeff' }}>
                                      <th style={{ padding: '5px 8px', textAlign: 'left' }}>Goal</th>
                                      <th style={{ padding: '5px 8px', textAlign: 'left' }}>Filename</th>
                                      <th style={{ padding: '5px 8px', textAlign: 'left' }}>Uploaded</th>
                                      <th style={{ padding: '5px 8px', textAlign: 'left' }}>Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(monthDeliveries[key] || []).map(d => (
                                      <tr key={d.id}>
                                        <td style={{ padding: '4px 8px' }}>{d.goal_name || d.goal_id}</td>
                                        <td style={{ padding: '4px 8px' }}>{d.filename || '—'}</td>
                                        <td style={{ padding: '4px 8px' }}>{d.upload_date ? d.upload_date.slice(0, 10) : '—'}</td>
                                        <td style={{ padding: '4px 8px' }}>
                                          <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', background: d.status === 'Delivered to PM' ? '#d5f5e3' : '#fef9e7', color: d.status === 'Delivered to PM' ? '#1e8449' : '#9a7d0a' }}>
                                            {d.status}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {(monthlySummary.monthly_summary || []).length === 0 && (
                      <tr><td colSpan={3} style={{ padding: '24px', textAlign: 'center', color: '#aaa' }}>No delivery history.</td></tr>
                    )}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
